import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import * as moment from 'moment';
import axios from 'axios';
import WebinarProp from '../propTypes/WebinarProp';
import translate from '../translate';
import SessionProp, {
  PARTICIPATION_STAT,
} from '../../sections/sessions/propTypes/sessionProp';
import { compose, getLink } from '../../services/utilityFunctions';
import QRCodeComponent from '../widgets/QRCodeComponent';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import { checkIfHasCreatorAccess } from '../../sections/sessions/propTypes/sessionUtils';
import Notifications from '../../services/notifications';
import withCSRF from '../withCSRF';

function SessionWebinarPlayer({
  t,
  _csrf,
  webinar,
  title,
  session,
  handleQRModal,
  isUserInParticipants,
  journeySlug,
}) {
  const { loggedUser } = useContext(LoggedUserContext);
  const isWebex = (webinar && webinar.provider === 'webex') || false;

  const link = webinar.url ? getLink(webinar.url) : '';

  const {
    confirmationLinkTimeStatus,
    confirmationLink = null,
    name,
    adminStatus,
    participantsJoined,
    participationStatus,
  } = session;

  const [sessionJoinedInfos, setAlreadyJoined] = useState({
    alreadyJoined: participantsJoined.find(u => loggedUser._id === u),
    confirmationLinkTimeStatus,
  });
  const handleWebinarClick = async () => {
    // If user didnt joined yet
    if (!sessionJoinedInfos.alreadyJoined) {
      await axios
        .put(
          `/api/access-session/${session._id}/participant-joined`,
          {},
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        )
        .then(({ data: response }) => {
          setAlreadyJoined({
            alreadyJoined: true,
            confirmationLinkTimeStatus:
              response.data.confirmationLinkTimeStatus,
          });
        })
        .catch(err => {
          Notifications.showErrorFromRequest(
            err,
            'error.session.confirmJoined',
          );
        });
    }
    if (!isWebex) {
      window.open(link, '_blank');
      return;
    }
    const width = window.screen.width / 1.5;
    const { height } = window.screen;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;
    window.open(
      webinar.url,
      title,
      `width=${width},height=${height},left=${left},top=${top}`,
    );
  };

  const openQRModal = () => {
    handleQRModal(confirmationLink);
  };

  const handleConfirmationLink = confirmationLinkHref => {
    let linkHref = confirmationLinkHref;
    if (journeySlug) {
      linkHref += `?journeySlug=${journeySlug}`;
    }
    window.location.href = linkHref;
  };
  const confirmationHint = !isWebex
    ? t('session.webinar.ongoing.confirm.participation')
    : t('session.webinar.ongoing.confirm.participation.webex');
  const handleStatus = () => {
    switch (adminStatus) {
      case 'planned':
      case 'confirmed':
        return (
          <>
            <div className="webinar-status-description">
              {t('academy_webinar_unStarted')}
            </div>
            <div className="webinar-date">
              {moment(session.startDate).format('DD MMM YYYY HH:mm')}
            </div>
          </>
        );
      case 'ongoing':
      case 'finished':
        return (
          <>
            <div className="webinar-status-description">
              {adminStatus === 'ongoing' ? (
                <div>
                  <div>
                    <span>{t('session.webinar.ongoing')}</span>
                    <span className="webinar-status-description-now">
                      {t('session.webinar.ongoing.now')}
                    </span>
                  </div>
                  {isWebex && (
                    <div className="webex-hint-email">
                      <span>{t('session.webinar.ongoing.hint.webex')}</span>
                      <span className="email">{loggedUser.email}</span>
                    </div>
                  )}
                </div>
              ) : (
                <span>{t('sessionDetails_webinar_finished')}</span>
              )}
            </div>
            {adminStatus === 'ongoing' && (
              <button
                type="button"
                className="webinar-button btn m-btn--pill btn-dynamic"
                onClick={handleWebinarClick}
              >
                {t('academy_webinar_join')}
              </button>
            )}
            {isUserInParticipants &&
              sessionJoinedInfos.confirmationLinkTimeStatus &&
              participationStatus !== PARTICIPATION_STAT.PARTICIPATED && (
                <div>
                  <span className="confirmation-title">{confirmationHint}</span>
                  <div className="confirm-participation row">
                    <div className="col-12 col-lg-5 containers">
                      <QRCodeComponent
                        value={confirmationLink}
                        buttonText={t('access_session.detail.qrButton')}
                        onClick={() => {
                          handleQRModal(confirmationLink);
                        }}
                        hasButton
                        size={99}
                        btnClass="qrWebinar-btn"
                      />
                    </div>
                    <div className="col-12 col-lg-1 containers">
                      <span className="span-or">{t('or')}</span>
                    </div>
                    <div className="col-12 col-lg-5 containers container-btn">
                      <button
                        type="button"
                        onClick={() => handleConfirmationLink(confirmationLink)}
                        className="btn btn-outline-dynamic confirmation-btn"
                      >
                        {t('session.webinar.ongoing.confirm')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
          </>
        );
      default:
        return <span />;
    }
  };
  const qrCodeNameImg = `${name}_QR.png`;
  return (
    <div className="webinar-container">
      <img className="webinar-preview" alt={title} />
      {handleStatus(webinar)}
      {checkIfHasCreatorAccess(session, loggedUser._id) && (
        <QRCodeComponent
          value={confirmationLink}
          title={t('access_session.detail.qrTitle')}
          buttonText={t('access_session.detail.qrButton')}
          onClick={openQRModal}
          hasButton
          hasDownload
          downloadImageName={qrCodeNameImg}
          size={125}
        />
      )}
    </div>
  );
}

SessionWebinarPlayer.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  handleQRModal: PropTypes.func.isRequired,
  webinar: PropTypes.shape(WebinarProp.propType).isRequired,
  session: PropTypes.shape(SessionProp.propType).isRequired,
  title: PropTypes.string,
  isUserInParticipants: PropTypes.bool,
  journeySlug: PropTypes.string, // in case of session in journey step
};

SessionWebinarPlayer.defaultProps = {
  title: '',
  isUserInParticipants: false,
  journeySlug: null,
};

export default compose(translate, withCSRF)(SessionWebinarPlayer);
